import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogEmphasis,
  DialogHeader,
  DialogTitle,
  DialogWarning,
} from 'components/ui';
import type { FirmDocumentDeleteDialogProps } from './FirmDocumentDeleteDialog.props';
import FirmDocumentDialogDocumentList from './FirmDocumentDialogDocumentList';
import { FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS } from './FirmDocumentFileActionDialogs.testIds';

type DeleteDialogContentState = Pick<FirmDocumentDeleteDialogProps, 'documentCount' | 'fileName' | 'fileNames'>;

const getDeleteTitle = (documentCount: number) =>
  documentCount === 1 ? 'Delete Document' : `Delete ${documentCount} Documents`;

const renderDeleteDescription = (documentCount: number, fileName?: string, fileNames?: string[]) => {
  if (documentCount === 1 && fileName) {
    return (
      <>
        <span>
          Are you sure you want to delete the <DialogEmphasis>{fileName}</DialogEmphasis> document?
        </span>
        <DialogWarning>
          This action cannot be undone.
        </DialogWarning>
      </>
    );
  }

  return (
    <>
      <span>{`Are you sure you want to delete ${documentCount} selected documents?`}</span>
      <FirmDocumentDialogDocumentList fileNames={fileNames} />
      <DialogWarning>
        This action cannot be undone.
      </DialogWarning>
    </>
  );
};

const FirmDocumentDeleteDialog = ({
  documentCount,
  fileName,
  fileNames,
  isDeleting = false,
  onClose,
  onConfirm,
  open,
}: FirmDocumentDeleteDialogProps) => {
  const [visibleContent, setVisibleContent] = useState<DeleteDialogContentState>({
    documentCount,
    fileName,
    fileNames,
  });

  // Preserve the previous dialog copy while the close animation runs with empty selection state.
  useEffect(() => {
    if (open && documentCount > 0) {
      setVisibleContent({
        documentCount,
        fileName,
        fileNames,
      });
    }
  }, [documentCount, fileName, fileNames, open]);

  const dialogContent = open && documentCount > 0
    ? { documentCount, fileName, fileNames }
    : visibleContent;

  return (
    <Dialog
      data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.deleteDialog}
      maxWidth="xs"
      onClose={isDeleting ? undefined : onClose}
      open={open}>
      <DialogHeader>
        <DialogTitle>{getDeleteTitle(dialogContent.documentCount)}</DialogTitle>
        <DialogCloseButton
          aria-label="Close delete document dialog"
          data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.deleteCloseButton}
          disabled={isDeleting}
          onClick={onClose}
          size="small">
          <DialogCloseIcon />
        </DialogCloseButton>
      </DialogHeader>
      <DialogContent>
        <DialogDescription>
          {renderDeleteDescription(
            dialogContent.documentCount,
            dialogContent.fileName,
            dialogContent.fileNames
          )}
        </DialogDescription>
      </DialogContent>
      <DialogActionFooter
        actionButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.deleteConfirmButton}
        actionLabel={dialogContent.documentCount === 1 ? 'Delete Document' : 'Delete Documents'}
        actionVisualVariant="danger"
        cancelButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.deleteCancelButton}
        isActionLoading={isDeleting}
        isCancelDisabled={isDeleting}
        onAction={onConfirm}
        onCancel={onClose}
      />
    </Dialog>
  );
};

export default FirmDocumentDeleteDialog;
